import { useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TimedFeePlan, TimedFeeTable } from '@pokerroom/types';
import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { useAuthStore, sessionVenueKey } from '../../store/auth.store';
import { api } from '../../lib/api';
import { TimedFeeEditor } from './TimedFeeEditor';

export function useFeePlans() {
  const venueId = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const session = useAuthStore((s) => s.sessionRevision);
  return useQuery({
    queryKey: ['timed-fee-plans', venueId, session],
    queryFn: () => api.get<TimedFeePlan[]>('/tournament/timed-fee-plans'),
  });
}

export default function TimedFeePlansPage() {
  const pages = useAuthStore((s) => s.visiblePages);
  const venueId = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const session = useAuthStore((s) => s.sessionRevision);
  if (!pages) return null;
  if (!pages.includes('/tournament/timed-fee-plans'))
    return <Navigate to={pages[0] ?? '/'} replace />;
  return <FeePlansWorkspace key={`${venueId}:${session}`} />;
}

function FeePlansWorkspace() {
  const { t } = useTranslation('tournament');
  const queryClient = useQueryClient();
  const actions = useAuthStore((s) => s.allowedActions);
  const canCreate = actions?.includes('template:create') ?? false;
  const canUpdate = actions?.includes('template:update') ?? false;
  const plans = useFeePlans();
  const [editing, setEditing] = useState<TimedFeePlan | null>();
  const [name, setName] = useState('');
  const [principal, setPrincipal] = useState('');
  const [table, setTable] = useState<TimedFeeTable>();
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const busy = useRef(false);
  const mutation = useMutation({
    mutationFn: (work: { kind: 'create' | 'publish' | 'archive'; plan?: TimedFeePlan }) => {
      if (work.kind === 'archive')
        return api.patch(`/tournament/timed-fee-plans/${work.plan!.id}`, {
          expectedSequence: work.plan!.latestSequence,
        });
      const body = { table: table ? { ...table, principalAmount: principal } : undefined };
      return work.kind === 'create'
        ? api.post('/tournament/timed-fee-plans', { ...body, name })
        : api.post(`/tournament/timed-fee-plans/${editing!.id}/versions`, {
            ...body,
            expectedSequence: editing!.latestSequence,
          });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timed-fee-plans'] });
      setEditing(undefined);
      setError('');
      setNotice(t('feePlans.saved'));
    },
    onSettled: () => {
      busy.current = false;
    },
    onError: (err: Error) => {
      const message: unknown = isAxiosError(err) ? err.response?.data?.message : undefined;
      setError(typeof message === 'string' ? message : err.message);
    },
  });
  const submitWork = (work: Parameters<typeof mutation.mutate>[0]) => {
    if (busy.current) return;
    busy.current = true;
    mutation.mutate(work);
  };
  const begin = (plan: TimedFeePlan | null) => {
    setEditing(plan);
    setName(plan?.name ?? '');
    setTable(plan?.versions[0]?.table);
    setPrincipal(plan?.versions[0]?.table.principalAmount ?? '');
    setError('');
    setNotice('');
  };
  return (
    <AdminPage>
      <AdminPageHeader title={t('feePlans.title')} description={t('feePlans.description')} />
      {canCreate && (
        <button
          type="button"
          className="border border-primary bg-primary px-4 py-2 text-primary-content disabled:opacity-50 mb-4"
          disabled={mutation.isPending}
          onClick={() => begin(null)}
        >
          {t('feePlans.create')}
        </button>
      )}
      {notice && <p role="status">{notice}</p>}
      {(plans.isError || error) && (
        <div role="alert" className="border border-error p-3 my-3 text-error">
          <p>{error || t('feePlans.loadError')}</p>
          <p>{t('feePlans.reloadHelp')}</p>
          <button
            type="button"
            className="border border-base-300 px-3 py-2 disabled:opacity-50"
            disabled={mutation.isPending}
            onClick={() => {
              void plans.refetch();
            }}
          >
            {t('feePlans.reload')}
          </button>
        </div>
      )}
      {editing !== undefined &&
        ((editing === null && canCreate) || (editing !== null && canUpdate)) && (
          <form
            className="border border-base-300 p-4 mb-4 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (mutation.isPending) return;
              if (!table) {
                setError(t('timedFees.required'));
                return;
              }
              submitWork({ kind: editing ? 'publish' : 'create' });
            }}
          >
            <h2 className="font-semibold">
              {editing ? t('feePlans.publish') : t('feePlans.create')}
            </h2>
            <label className="flex flex-col gap-1 min-w-0">
              <span>{t('feePlans.name')}</span>
              <input
                className="w-full border border-base-300 bg-base-100 px-3 py-2 text-base-content focus:outline-2 focus:outline-primary disabled:opacity-60"
                required
                maxLength={128}
                value={name}
                disabled={!!editing || mutation.isPending}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 min-w-0">
              <span>{t('feeSummary.principal')}</span>
              <input
                className="w-full border border-base-300 bg-base-100 px-3 py-2 text-base-content focus:outline-2 focus:outline-primary disabled:opacity-60"
                inputMode="decimal"
                required
                value={principal}
                disabled={mutation.isPending}
                onChange={(event) => setPrincipal(event.target.value)}
              />
            </label>
            <fieldset disabled={mutation.isPending}>
              <TimedFeeEditor
                value={table}
                principal={principal}
                legacyRake="0"
                onChange={setTable}
              />
            </fieldset>
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                className="border border-primary bg-primary px-4 py-2 text-primary-content disabled:opacity-50"
                disabled={mutation.isPending}
              >
                {editing ? t('feePlans.publish') : t('feePlans.create')}
              </button>
              <button
                type="button"
                className="border border-base-300 px-4 py-2 hover:bg-base-300 disabled:opacity-50"
                disabled={mutation.isPending}
                onClick={() => {
                  setEditing(undefined);
                  setError('');
                }}
              >
                {t('feePlans.cancel')}
              </button>
            </div>
          </form>
        )}
      {plans.isLoading && <p role="status">{t('feePlans.loading')}</p>}
      {!plans.isLoading && !plans.isError && !plans.data?.length && <p>{t('feePlans.empty')}</p>}
      <ul className="space-y-4">
        {plans.data?.map((plan) => (
          <li key={plan.id} className="border border-base-300 p-4 space-y-3">
            <h2 className="font-semibold">
              {plan.name} · {t(`feePlans.${plan.status.toLowerCase()}`)}
            </h2>
            <p>
              {t('feePlans.version')} {plan.latestSequence} · {t('feeSummary.principal')}{' '}
              {plan.versions[0]?.table.principalAmount}
            </p>
            {plan.status === 'ACTIVE' && canUpdate && (
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  className="border border-base-300 px-4 py-2 hover:bg-base-300 disabled:opacity-50"
                  disabled={mutation.isPending}
                  onClick={() => begin(plan)}
                >
                  {t('feePlans.edit')}
                </button>
                <button
                  type="button"
                  className="border border-base-300 px-4 py-2 hover:bg-base-300 disabled:opacity-50"
                  disabled={mutation.isPending}
                  onClick={() => submitWork({ kind: 'archive', plan })}
                >
                  {t('feePlans.archive')}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </AdminPage>
  );
}
